import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface NotificationInput {
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  link?: string;
  icon?: React.ReactNode;
  timestamp?: Date;
}

export interface Notification extends NotificationInput {
  id: string;
  read: boolean;
  createdAt: Date;
}

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: NotificationInput) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  devtools(
    persist(
      (set) => ({
        notifications: [],
        unreadCount: 0,
        
        addNotification: (notification) =>
          set((state) => {
            const newNotification: Notification = {
              ...notification,
              id: Math.random().toString(36).substr(2, 9),
              read: false,
              createdAt: new Date(),
            };
            
            return {
              notifications: [newNotification, ...state.notifications].slice(0, 100), // Limit to 100 notifications
              unreadCount: state.unreadCount + 1,
            };
          }),

        markAsRead: (id) =>
          set((state) => {
            // Only update if the notification exists and is unread
            const notification = state.notifications.find(n => n.id === id);
            if (!notification || notification.read) {
              return state; // No changes needed
            }
            
            // Create a new array with the updated notification
            const updatedNotifications = state.notifications.map(n => 
              n.id === id ? { ...n, read: true } : n
            );
            
            return {
              notifications: updatedNotifications,
              unreadCount: Math.max(0, state.unreadCount - 1),
            };
          }),

        markAllAsRead: () =>
          set((state) => ({
            notifications: state.notifications.map((n) => ({ ...n, read: true })),
            unreadCount: 0,
          })),

        removeNotification: (id) =>
          set((state) => {
            const removed = state.notifications.find((n) => n.id === id);
            return {
              notifications: state.notifications.filter((n) => n.id !== id),
              unreadCount: removed && !removed.read ? state.unreadCount - 1 : state.unreadCount,
            };
          }),

        clearAll: () => set({ notifications: [], unreadCount: 0 }),
      }),
      {
        name: 'notifications-storage',
        partialize: (state) => ({
          notifications: state.notifications,
          unreadCount: state.unreadCount,
        }),
      }
    )
  )
);


